import * as Api from '@ViewModels';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IModalContext } from '../../../../models';
import { asModalComponent } from '../../Modal';
import {
	SocialMediaImagesPreviewAlert,
	SocialMediaImagesPreviewImages,
	SocialMediaImagesPreviewRadioButtons,
	SocialMediaLogoSizeControls,
} from './presentation';

interface IProps extends IModalContext<Api.IFileAttachment> {
	images?: Api.IFileAttachment[];
	initialImageUrl?: string;
	showOverlayLogo?: boolean;
	logoUrl: string;
	hideWhiteBackground: boolean;
	logoSize?: number;
	onLogoSizeChange?: (size: number) => void;
}

function _SocialMediaImagesPreviewModal({
	images,
	initialImageUrl,
	showOverlayLogo,
	logoUrl,
	hideWhiteBackground,
	logoSize = Api.DEFAULT_LOGO_SIZE,
	onLogoSizeChange,
}: IProps) {
	const foundIndex = initialImageUrl ? (images?.findIndex(i => i.url === initialImageUrl) ?? 0) : 0;
	const [currentImageIndex, setCurrentImageIndex] = React.useState<number>(foundIndex >= 0 ? foundIndex : 0);
	const [tempLogoSize, setTempLogoSize] = React.useState<number>(logoSize);

	// Sync temp size with prop changes
	React.useEffect(() => {
		setTempLogoSize(logoSize);
	}, [logoSize]);

	const onImageIndexChange = (index: number) => () => {
		setCurrentImageIndex(index);
	};

	const handleLogoSizeChange = (size: number) => {
		setTempLogoSize(size);
	};

	const handleSaveLogoSize = () => {
		onLogoSizeChange?.(tempLogoSize);
	};

	const handleCancelLogoSize = () => {
		setTempLogoSize(logoSize);
	};

	return (
		<div>
			<div>
				{showOverlayLogo ? <SocialMediaImagesPreviewAlert /> : null}
				<SocialMediaImagesPreviewImages
					images={images}
					currentImageIndex={currentImageIndex}
					showOverlayLogo={showOverlayLogo}
					url={logoUrl}
					onImageIndexChange={onImageIndexChange}
					hideWhiteBackground={hideWhiteBackground}
					logoSizeValue={tempLogoSize}
				/>
				{images?.length > 1 ? (
					<SocialMediaImagesPreviewRadioButtons
						key='images-radio-buttons'
						currentImageIndex={currentImageIndex}
						images={images}
						onImageIndexChange={onImageIndexChange}
					/>
				) : null}
				{showOverlayLogo && !images?.some(x => x.hasLogoOverlay) ? (
					<SocialMediaLogoSizeControls
						logoSizeValue={tempLogoSize}
						contextLogoSize={logoSize}
						onLogoSizeChange={handleLogoSizeChange}
						onSaveLogoSize={handleSaveLogoSize}
						onCancelLogoSize={handleCancelLogoSize}
					/>
				) : null}
			</div>
		</div>
	);
}

const SocialMediaImagesPreview = observer(_SocialMediaImagesPreviewModal);

export const SocialMediaImagesPreviewModal = asModalComponent(SocialMediaImagesPreview, {
	className: `social-media-images-preview-modal`,
	shouldCloseOnOverlayClick: true,
	useDefaultHeader: true,
});
